/* ============================================================================
 * ReportKit — ชิ้นส่วนที่ใช้ร่วมกันของ "หน้ารายงาน" ทุกหน้า
 *
 * ทำไมต้องแยกไฟล์นี้ออกมา:
 *   หน้ารายงานทุกหน้าใช้โครงเดียวกัน (hero + ตัวกรองช่วงเวลา + KPI + กราฟ + ตาราง)
 *   ถ้าปล่อยให้แต่ละหน้าเขียนเอง จะเพี้ยนกันทีละนิดจนหน้าตาไม่เป็นชุดเดียวกัน
 *   ไฟล์นี้จึงถือ design token + primitive + ตัวกรองช่วงเวลาไว้ที่เดียว
 *
 * ใช้ token ชุดเดียวกับแดชบอร์ด (ink #142016 / lime #a3e635) และ reuse
 * คลาส dash-* จาก assets/css/dashboard.css — หน้ารายงานจึงต้อง import
 * ทั้ง dashboard.css และ report.css
 * ========================================================================== */

import React, { Component } from "react";
import CountUp from "react-countup";
import { t } from "../CustomComponent.js";

/* ------------------------------------------------------------ design token */

export const BRAND = {
  ink: "#142016",
  lime: "#a3e635",
  limeSoft: "#eaf9d2",
  line: "#e6eae7",
  muted: "#8b9491",
  track: "#eef1ec",
};

/* สีของ "ช่องสถานะ" ในรายงาน — ยุบสถานะจริง 6 ค่าเหลือ 4 ช่อง (ตรงกับฝั่ง API)
     available  = ว่าง
     reserved   = ติดจอง + อยู่ระหว่างดำเนินการ
     closed     = ขายแล้ว + ปล่อยเช่าแล้ว
     off_market = ปิดประกาศ
   สีอิงชุดกลางใน asset_option.js (เหลืองปรับเข้มขึ้นให้อ่านออกบนพื้นขาวของกราฟ) */
export const STATUS = {
  available: "#16a34a",
  reserved: "#ca8a04",
  closed: "#dc2626",
  off_market: "#111827",
};

export const STATUS_TINT = {
  available: "#dcfce7",
  reserved: "#fef9c3",
  closed: "#fee2e2",
  off_market: "#e5e7eb",
};

// ไล่เฉด lime 8 ระดับ สำหรับจำแนกหมวด (ประเภททรัพย์ / ช่องทาง)
export const TYPE_RAMP = ["#1a2e05", "#365314", "#4d7c0f", "#65a30d", "#84cc16", "#a3e635", "#bef264", "#d9f99d"];

/* --------------------------------------------------------------- formatter */

export const num = (n) => Number(n || 0).toLocaleString("en-US");

export const share = (part, whole) => (!whole ? 0 : Math.round((Number(part) / Number(whole)) * 100));

// ทศนิยม 1 ตำแหน่ง ใช้กับอัตราส่วนที่ปัดเป็นจำนวนเต็มแล้วเสียความหมาย
export const share1 = (part, whole) => (!whole ? 0 : Math.round((Number(part) / Number(whole)) * 1000) / 10);

export const money = (n) => `฿${num(Math.round(Number(n || 0)))}`;

// ย่อหลักล้านสำหรับตัวเลขใหญ่ในการ์ด KPI — เต็ม ๆ ให้ไปอ่านในตาราง
export const moneyShort = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1000000) return `฿${(Math.round((v / 1000000) * 100) / 100).toLocaleString("en-US")} ${t("unit_million")}`;
  return money(v);
};

/* -------------------------------------------------------------- วันที่ */

const TH_MONTH_SHORT = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
const TH_MONTH_FULL = ["มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน", "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"];
const TH_DOW = ["อา.", "จ.", "อ.", "พ.", "พฤ.", "ศ.", "ส."];
const EN_MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const EN_MONTH_FULL = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const EN_DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

export const isTh = () => (localStorage.getItem("lang") || "TH") !== "EN";
export const monthShort = (i) => (isTh() ? TH_MONTH_SHORT[i] : EN_MONTH_SHORT[i]);

export const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
export const sameDay = (a, b) => !!a && !!b && a.toDateString() === b.toDateString();
export const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
export const dayDiff = (a, b) => Math.round((startOfDay(b) - startOfDay(a)) / 86400000);

export const fmtDate = (d) =>
  !d
    ? ""
    : isTh()
    ? `${d.getDate()} ${TH_MONTH_SHORT[d.getMonth()]} ${d.getFullYear() + 543}`
    : `${d.getDate()} ${EN_MONTH_SHORT[d.getMonth()]} ${d.getFullYear()}`;

export const fmtRange = (s, e) => (!s ? "-" : !e || sameDay(s, e) ? fmtDate(s) : `${fmtDate(s)} - ${fmtDate(e)}`);

const fmtMonthTitle = (d) =>
  isTh() ? `${TH_MONTH_FULL[d.getMonth()]} ${d.getFullYear() + 543}` : `${EN_MONTH_FULL[d.getMonth()]} ${d.getFullYear()}`;

// เวลาที่ออกรายงาน — ใส่ท้ายรายงานทุกหน้าเพื่อให้รู้ว่าข้อมูลเป็น ณ ตอนไหน
export const fmtStamp = (d = new Date()) => {
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${fmtDate(d)} ${hh}:${mm}`;
};

/* --------------------------------------------------------- ช่วงเวลามาตรฐาน */

export const PERIODS = [
  { key: "today", label: "period_today" },
  { key: "d7", label: "period_7d" },
  { key: "month", label: "period_this_month" },
  { key: "year", label: "period_this_year" },
];

export const presetRange = (key) => {
  const today = startOfDay(new Date());
  switch (key) {
    case "today":
      return { start: today, end: today };
    case "d7":
      return { start: addDays(today, -6), end: today };
    case "year":
      return { start: new Date(today.getFullYear(), 0, 1), end: today };
    case "month":
    default:
      return { start: new Date(today.getFullYear(), today.getMonth(), 1), end: today };
  }
};

// รูปแบบที่ API รับ — ต้องเป็นวันที่ตามปฏิทินที่ผู้ใช้เห็น จึงประกอบเองจาก
// getFullYear/getMonth/getDate ไม่ใช้ toISOString() ที่แปลงเป็น UTC แล้วเลื่อนไปหนึ่งวัน
export const toISODate = (d) =>
  !d ? "" : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// YYYY-MM-DD -> Date ของวันนั้นตามปฏิทินผู้ใช้ (คู่ตรงข้ามของ toISODate)
// new Date("YYYY-MM-DD") ตีความเป็น UTC ซึ่งกลายเป็นคนละวันในโซนเวลาไทย
const fromISODate = (value) => {
  const [y, m, d] = String(value || "").split("-").map(Number);
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * ช่วงเวลาที่ส่งต่อมากับ url (เช่นกดปุ่ม "ดูรายงานเต็ม" จากแดชบอร์ด)
 * รองรับทั้ง ?period=<preset> และ ?start=&end= สำหรับช่วงที่ผู้ใช้เลือกเอง
 * ไม่มีหรือค่าไม่ถูกต้อง = คืน null ให้หน้าใช้ค่าตั้งต้นของตัวเองไปตามเดิม
 */
export const rangeFromSearch = (search) => {
  const params = new URLSearchParams(search || "");
  const period = params.get("period");

  if (period && PERIODS.some((p) => p.key === period)) {
    return { period, ...presetRange(period) };
  }

  const start = fromISODate(params.get("start"));
  const end = fromISODate(params.get("end"));
  if (!start) return null;

  return { period: "custom", start, end: end && end >= start ? end : start };
};

/**
 * ป้ายกำกับแกนเวลาของกราฟ
 * API ส่งจุดกราฟมาเป็นเวลา ISO พร้อมหน่วยของแกน (hour | day | week | month)
 * แล้วให้หน้าเว็บจัดรูปแบบเอง เพราะรูปแบบวันที่ขึ้นกับภาษาที่ผู้ใช้เลือก
 */
export const seriesLabel = (value, unit) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";

  switch (unit) {
    case "hour":
      return `${String(d.getHours()).padStart(2, "0")}:00`;
    case "month":
      return monthShort(d.getMonth());
    case "week":
      return `${d.getDate()} ${monthShort(d.getMonth())}`;
    case "day":
    default:
      return String(d.getDate());
  }
};

// ชื่อประเภททรัพย์/ทำเลที่ API ส่งมาทั้งไทยและอังกฤษ — เลือกตามภาษาที่ใช้อยู่
export const localName = (row) => (row ? (isTh() ? row.name_th : row.name_en || row.name_th) : "-");

// ค่าสูงสุดของชุดข้อมูล ใช้เป็นตัวหารของแถบสัดส่วน — รายการว่างต้องได้ 0 ไม่ใช่ -Infinity
export const maxOf = (rows = [], pick = (x) => x) => rows.reduce((max, row) => Math.max(max, Number(pick(row)) || 0), 0);

/* ------------------------------------------------------------- primitives */

// ตัวเลขวิ่งขึ้นตอนโหลดและตอนเปลี่ยนตัวกรอง — ทำให้เห็นชัดว่าค่าอัปเดตแล้ว
export const Figure = ({ value, prefix = "", suffix = "", decimals = 0, className = "", style }) => (
  <span className={`dash-figure ${className}`} style={style}>
    <CountUp end={Number(value) || 0} duration={1.1} separator="," decimals={decimals} prefix={prefix} suffix={suffix} preserveValue />
  </span>
);

export const Chip = ({ icon, color = BRAND.ink, tint = BRAND.limeSoft, size = 40 }) => (
  <span
    className="dash-chip icon flex-shrink-0"
    style={{ width: `${size}px`, height: `${size}px`, backgroundColor: tint, color, fontSize: `${Math.round(size * 0.42)}px` }}
  >
    {icon}
  </span>
);

export const Delta = ({ value, suffix = "%" }) => {
  const up = Number(value) >= 0;
  return (
    <span className={`dash-delta ${up ? "dash-delta--up" : "dash-delta--down"}`}>
      <span className="icon text-10">{up ? "" : ""}</span>
      {Math.abs(Number(value)).toFixed(1)}
      {suffix}
    </span>
  );
};

export const Bar = ({ value, max, color = BRAND.ink, height = 6, track }) => (
  <div className="dash-track w-100" style={{ height: `${height}px`, backgroundColor: track }}>
    <div
      className="dash-fill"
      style={{ height: `${height}px`, width: `${share(value, max)}%`, backgroundColor: color, minWidth: value ? "4px" : 0 }}
    />
  </div>
);

// แถบสัดส่วนหลายสีในบรรทัดเดียว — ใช้แทนกราฟแท่งซ้อนในตาราง
export const StackBar = ({ segments, height = 8 }) => {
  const total = segments.reduce((s, x) => s + Number(x.value || 0), 0);
  return (
    <div className="rpt-stack" style={{ height: `${height}px` }}>
      {segments.map((s, i) => (
        <span
          key={i}
          title={`${s.label} ${num(s.value)} (${share(s.value, total)}%)`}
          style={{ width: `${share(s.value, total)}%`, backgroundColor: s.color, minWidth: s.value ? "4px" : 0 }}
        />
      ))}
    </div>
  );
};

export const Card = ({ children, className = "", style }) => (
  <div className={`dash-card h-100 ${className}`} style={style}>
    {children}
  </div>
);

export const Section = ({ title, note }) => (
  <div className="d-flex align-items-center gap-3 mb-3 mt-4">
    <span className="dash-section__title">
      <span className="dash-section__dot" />
      <span className="fw-bold text-dark text-16 text-nowrap">{title}</span>
    </span>
    {note && <span className="text-12 text-secondary text-nowrap d-none d-md-inline">{note}</span>}
    <div className="dash-section__rule flex-grow-1" />
  </div>
);

export const CardTitle = ({ title, note, right }) => (
  <div className="d-flex align-items-start justify-content-between mb-3 gap-2">
    <div style={{ minWidth: 0 }}>
      <h6 className="fw-bold mb-0 text-dark text-14">{title}</h6>
      {note && <div className="text-10 text-secondary mt-1">{note}</div>}
    </div>
    {right}
  </div>
);

/**
 * การ์ดตัวเลขหลัก 1 ช่อง
 * @param {string} label  หัวข้อ
 * @param {node}   value  ตัวเลขที่แสดง (ส่ง <Figure/> เข้ามาเพื่อให้นับขึ้น)
 * @param {string} unit   หน่วยต่อท้ายตัวเลข
 * @param {number} delta  % เทียบช่วงก่อนหน้า (ไม่ส่ง = ไม่แสดง)
 * @param {node}   foot   บรรทัดล่างสุด เช่น แถบสัดส่วน
 */
export const KpiTile = ({ label, value, unit, delta, icon, color = BRAND.ink, tint = BRAND.limeSoft, foot }) => (
  <Card>
    <div className="p-3 p-xl-4 d-flex flex-column h-100">
      <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
        <span className="text-12 text-secondary">{label}</span>
        <Chip icon={icon} color={color} tint={tint} size={36} />
      </div>
      <div className="d-flex align-items-baseline gap-1 flex-wrap">
        <span className="fw-bold text-dark rpt-kpi__value">{value}</span>
        {unit && <span className="text-10 text-secondary">{unit}</span>}
      </div>
      <div className="mt-auto pt-3">{foot || (delta !== undefined && delta !== null ? <Delta value={delta} /> : null)}</div>
    </div>
  </Card>
);

export const EmptyState = ({ text }) => (
  <div className="rpt-empty text-center py-5">
    <span className="icon d-block mb-2" style={{ fontSize: "26px", color: BRAND.muted }}>
      {""}
    </span>
    <span className="text-12 text-secondary">{text || t("no_data")}</span>
  </div>
);

/* ============================================================================
 * PeriodFilter — ปุ่มช่วงเวลาสำเร็จรูป + ปฏิทินเลือกช่วงเอง
 * controlled component: ส่ง value เข้า / รับค่าใหม่ทาง onChange
 *   value    = { period, start, end }   period เป็น key ของ PERIODS หรือ "custom"
 *   onChange = (next) => void
 * ========================================================================== */
export class PeriodFilter extends Component {
  constructor(props) {
    super(props);
    this.state = { open: false, cal_month: this.focusMonth(), hover_date: null };
    this.ref = React.createRef();
  }

  componentDidMount() {
    document.addEventListener("mousedown", this.handleOutsideClick);
    document.addEventListener("keydown", this.handleEsc);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleOutsideClick);
    document.removeEventListener("keydown", this.handleEsc);
  }

  focusMonth = () => {
    const { end, start } = this.props.value || {};
    const focus = end || start || new Date();
    return new Date(focus.getFullYear(), focus.getMonth(), 1);
  };

  handleOutsideClick = (e) => {
    if (this.state.open && this.ref.current && !this.ref.current.contains(e.target)) this.close();
  };

  handleEsc = (e) => {
    if (e.key === "Escape" && this.state.open) this.close();
  };

  open = () => {
    this.snapshot = { ...this.props.value };
    this.setState({ open: true, cal_month: this.focusMonth(), hover_date: null });
  };

  // ปิดโดยที่ยังเลือกไม่ครบ 2 วัน = ยกเลิก กลับไปใช้ช่วงเดิม
  close = () => {
    if (!this.props.value?.end && this.snapshot) this.props.onChange(this.snapshot);
    this.setState({ open: false, hover_date: null });
  };

  shiftMonth = (n) =>
    this.setState((s) => ({ cal_month: new Date(s.cal_month.getFullYear(), s.cal_month.getMonth() + n, 1) }));

  monthCells = (base) => {
    const first = new Date(base.getFullYear(), base.getMonth(), 1);
    const cells = new Array(first.getDay()).fill(null);
    const d = new Date(first);
    while (d.getMonth() === first.getMonth()) {
      cells.push(new Date(d));
      d.setDate(d.getDate() + 1);
    }
    return cells;
  };

  pickPreset = (key) => {
    const r = presetRange(key);
    this.setState({ open: false, hover_date: null, cal_month: new Date(r.end.getFullYear(), r.end.getMonth(), 1) });
    this.props.onChange({ period: key, start: r.start, end: r.end });
  };

  // คลิกแรก = วันเริ่ม, คลิกที่สอง = วันสิ้นสุด (คลิกย้อนหลังจะสลับให้อัตโนมัติ)
  pickDate = (date) => {
    const { start, end } = this.props.value || {};
    if (!start || end) {
      this.setState({ hover_date: null });
      this.props.onChange({ period: "custom", start: date, end: null });
      return;
    }
    const [s, e] = date < start ? [date, start] : [start, date];
    this.setState({ open: false, hover_date: null });
    this.props.onChange({ period: "custom", start: s, end: e });
  };

  renderCalendar() {
    const { period, start, end } = this.props.value || {};
    const { cal_month, hover_date } = this.state;
    const today = startOfDay(new Date());
    const cells = this.monthCells(cal_month);
    const dow = isTh() ? TH_DOW : EN_DOW;
    const atCurrentMonth = cal_month.getFullYear() === today.getFullYear() && cal_month.getMonth() === today.getMonth();
    // ระหว่างรอคลิกวันที่สอง ให้ไฮไลต์ช่วงตามตำแหน่งเมาส์
    const previewEnd = end || (start && hover_date && hover_date > start ? hover_date : null);
    const spanning = !!(start && previewEnd && !sameDay(start, previewEnd));

    const dayClass = (d) => {
      const isStart = sameDay(d, start);
      const isEnd = sameDay(d, end);
      const inRange = start && previewEnd && d > start && d < previewEnd;
      return [
        "dash-cal__day",
        d > today ? "is-disabled" : "",
        isStart || isEnd ? "is-edge" : "",
        inRange ? "is-in" : "",
        isStart && spanning ? "is-start" : "",
        isEnd && spanning ? "is-end" : "",
        sameDay(d, today) && !isStart && !isEnd ? "is-today" : "",
      ]
        .filter(Boolean)
        .join(" ");
    };

    return (
      <div className="dash-pop dash-pop--cal">
        <div className="dash-cal">
          <div className="dash-cal__presets">
            <div className="text-10 text-secondary px-2 pb-2">{t("quick_select")}</div>
            {PERIODS.map((p) => (
              <button
                key={p.key}
                type="button"
                className={`dash-pop__item ${p.key === period ? "is-active" : ""}`}
                onClick={() => this.pickPreset(p.key)}
              >
                <span>{t(p.label)}</span>
              </button>
            ))}
          </div>

          <div className="dash-cal__body">
            <div className="dash-cal__head">
              <button type="button" className="dash-cal__nav icon" aria-label={t("previous_month")} onClick={() => this.shiftMonth(-1)}>
                {""}
              </button>
              <span className="fw-bold text-14 text-dark">{fmtMonthTitle(cal_month)}</span>
              <button
                type="button"
                className="dash-cal__nav icon"
                aria-label={t("next_month")}
                disabled={atCurrentMonth}
                onClick={() => this.shiftMonth(1)}
              >
                {""}
              </button>
            </div>

            <div className="dash-cal__grid dash-cal__dow">
              {dow.map((d, i) => (
                <span key={i}>{d}</span>
              ))}
            </div>

            <div className="dash-cal__grid" onMouseLeave={() => this.setState({ hover_date: null })}>
              {cells.map((d, i) =>
                d ? (
                  <button
                    key={i}
                    type="button"
                    className={dayClass(d)}
                    disabled={d > today}
                    onMouseEnter={() => this.setState({ hover_date: d })}
                    onClick={() => this.pickDate(d)}
                  >
                    {d.getDate()}
                  </button>
                ) : (
                  <span key={i} />
                )
              )}
            </div>

            <div className="dash-cal__foot">
              <span className="text-12 text-dark fw-semibold">{start && !end ? t("pick_end_date") : fmtRange(start, end)}</span>
              <button type="button" className="dash-ghost-btn" onClick={() => this.pickPreset("month")}>
                {t("reset")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { period, start, end } = this.props.value || {};
    const { open } = this.state;
    return (
      <div className="d-flex flex-wrap align-items-center gap-2 gap-md-3">
        <div className="dash-seg" role="group" aria-label={t("select_period")}>
          {PERIODS.map((p) => (
            <button
              key={p.key}
              type="button"
              aria-pressed={p.key === period}
              className={`dash-seg__btn ${p.key === period ? "is-active" : ""}`}
              onClick={() => this.pickPreset(p.key)}
            >
              {t(p.label)}
            </button>
          ))}
        </div>

        <div className="position-relative" ref={this.ref}>
          <button
            type="button"
            className={`dash-range ${open ? "is-open" : ""}`}
            aria-expanded={open}
            aria-label={t("select_date_range")}
            onClick={() => (open ? this.close() : this.open())}
          >
            <span className="icon text-14" style={{ color: BRAND.muted }}>
              {""}
            </span>
            <span className="fw-semibold">{fmtRange(start, end)}</span>
            <span className="dash-range__chevron icon text-10">{""}</span>
          </button>
          {open && this.renderCalendar()}
        </div>
      </div>
    );
  }
}

/* ============================================================================
 * ReportHero — แถบหัวรายงานพื้นขาว : ชื่อรายงาน ช่วงข้อมูล
 * และตัวเลขสรุป 3 ตัวที่ต้องเห็นก่อนเสมอ
 *   headline = [{ label, value, unit }]
 * ========================================================================== */
export const ReportHero = ({ icon, title, subtitle, rangeText, headline = [] }) => (
  <div className="rpt-hero">
    <div className="rpt-hero__top">
      <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
        <span className="rpt-hero__icon icon">{icon}</span>
        <div style={{ minWidth: 0 }}>
          <h3 className="fw-bold mb-1 rpt-hero__title">{title}</h3>
          <div className="rpt-hero__sub text-14">{subtitle}</div>
        </div>
      </div>

      <span className="rpt-hero__range">
        <span className="icon text-12 me-2">{"\uf133"}</span>
        {rangeText}
      </span>
    </div>

    {!!headline.length && (
      <div className="rpt-hero__stats">
        {headline.map((h, i) => (
          <div className="rpt-hero__stat" key={i}>
            <div className="rpt-hero__stat-label">{h.label}</div>
            <div className="rpt-hero__stat-value dash-figure">
              {h.value}
              {h.unit && <span className="rpt-hero__stat-unit">{h.unit}</span>}
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);
